import { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from "react";

import { App_Url_New } from "@/shared/common/static";
import { ConfirmationCheckbox } from "@/shared/components";
import { DocumentUploadGrid } from "@/shared/components/DocumentUploadGrid";
import type { StepFormComponentProps, StepFormHandle } from "@/shared/stepper/types";
import type {
  DocumentUploadField,
  FieldKey,
  PersistedUploadedDocument,
} from "@/shared/types/documentuploadtype";
import {
  getNationalIdBackLabel,
  getNationalIdFrontLabel,
  getNationalIdLabel,
} from "@/shared/utils/nationalId";
import { useTravelPostQuoteData } from "../../hooks/useTravelPostQuoteData";
import type { TravelDocumentUploadValues } from "../../redux/type";
import { CommonAccordion } from "./TravellerAccordion";

type TravelDocumentUploadStepExtraProps = {
  isReadOnly?: boolean;
  persistedDocuments?: Partial<Record<string, PersistedUploadedDocument<string>>>;
  travelDetails?: {
    travellers?: Array<{ name?: string; dob?: string }>;
    coverage?: string;
    coverageLabel?: string;
  };
  onUploadComplete?: (params: {
    field: DocumentUploadField<TravelDocumentUploadValues>;
    affectedFields?: Array<DocumentUploadField<TravelDocumentUploadValues>>;
    file: File;
    uploadResponse?: unknown;
    ocrResponse?: unknown;
    persistedDocument: PersistedUploadedDocument<FieldKey<TravelDocumentUploadValues>>;
  }) => void;
  isShowUploadGuidelines?: boolean;
  showRequiredIdentificationTitle?: boolean;
  onConfirmDocumentsChange?: (checked: boolean) => boolean | Promise<boolean> | void;
  enableAutoConfirmWhenRequiredUploaded?: boolean;
};

const defaultValues: TravelDocumentUploadValues = {
  passport: false,
  passportBack: false,
  passportCombined: false,
  visaCopy: false,
  confirmDocuments: false,
  emiratesIdFront: false,
  emiratesIdBack: false,
  emiratesIdCombined: false,
};

const guidelines = [
  "Accepted formats: PDF, JPG, PNG",
  "Maximum file size: 5 MB per document",
  "Ensure all details are clearly visible",
  "Blurry or cropped documents may delay approval",
  "Documents must be clear, complete, and suitable for OCR validation",
];

function getTravellerLabel(index: number, traveller?: { name?: string; dob?: string }) {
  const title = index === 0 ? "Primary Traveller" : `Traveller ${index + 1}`;
  const details = [traveller?.name, traveller?.dob ? `DOB: ${traveller.dob}` : ""]
    .filter(Boolean)
    .join(" | ");

  return details ? `${title} (${details})` : title;
}

function getTravellerFieldKey(index: number, key: string) {
  return index === 0 ? key : `traveller_${index}_${key}`;
}

function getCombinedGroup(
  travellerIndex: number,
  id: string,
  label: string,
  isPrimary = false,
  key?: string
) {
  return {
    id: `${travellerIndex}-${id}`,
    key,
    label,
    checkboxLabel: "Both sides are in one file",
    combinedTitle: "Front & Back",
    combinedHelper: "Click to upload (PDF / JPG / PNG)",
    isPrimary,
  };
}

function createFields(
  travellerIndex: number
): Array<DocumentUploadField<TravelDocumentUploadValues>> {
  if (travellerIndex > 0) {
    return [
      {
        key: getTravellerFieldKey(travellerIndex, "passport"),
        label: "Passport",
        title: "Passport Front",
        helper: "Click to upload (PDF / JPG / PNG)",
        required: false,
        icon: App_Url_New.DocumentIcons.passport,
        ocrFields: App_Url_New.OcrApiFields.PASSPORT,
        combinedUploadGroup: getCombinedGroup(
          travellerIndex,
          "passport",
          "Passport",
          true,
          getTravellerFieldKey(travellerIndex, "passportCombined")
        ),
      },
      {
        key: getTravellerFieldKey(travellerIndex, "passportBack"),
        label: "Passport Back",
        title: "Passport Back",
        helper: "Click to upload (PDF / JPG / PNG)",
        required: false,
        icon: App_Url_New.DocumentIcons.passport,
        enableOcr: false,
        combinedUploadGroup: getCombinedGroup(
          travellerIndex,
          "passport",
          "Passport",
          false,
          getTravellerFieldKey(travellerIndex, "passportCombined")
        ),
      },
    ];
  }

  return [
    {
      key: getTravellerFieldKey(travellerIndex, "emiratesIdFront"),
      label: getNationalIdFrontLabel(),
      title: getNationalIdFrontLabel(),
      helper: "Click to upload (PDF / JPG / PNG)",
      required: false,
      icon: App_Url_New.DocumentIcons.idCard,
      ocrFields: App_Url_New.OcrApiFields.EMIRATES_ID,
      combinedUploadGroup: getCombinedGroup(
        travellerIndex,
        "emirates-id",
        getNationalIdLabel(),
        true,
        getTravellerFieldKey(travellerIndex, "emiratesIdCombined")
      ),
    },
    {
      key: getTravellerFieldKey(travellerIndex, "emiratesIdBack"),
      label: getNationalIdBackLabel(),
      title: getNationalIdBackLabel(),
      helper: "Click to upload (PDF / JPG / PNG)",
      required: false,
      icon: App_Url_New.DocumentIcons.idCard,
      enableOcr: false,
      combinedUploadGroup: getCombinedGroup(
        travellerIndex,
        "emirates-id",
        getNationalIdLabel(),
        false,
        getTravellerFieldKey(travellerIndex, "emiratesIdCombined")
      ),
    },
    {
      key: getTravellerFieldKey(travellerIndex, "passport"),
      label: "Passport",
      title: "Passport Front",
      helper: "Click to upload (PDF / JPG / PNG)",
      required: false,
      icon: App_Url_New.DocumentIcons.passport,
      ocrFields: App_Url_New.OcrApiFields.PASSPORT,
      combinedUploadGroup: getCombinedGroup(
        travellerIndex,
        "passport",
        "Passport",
        true,
        getTravellerFieldKey(travellerIndex, "passportCombined")
      ),
    },
    {
      key: getTravellerFieldKey(travellerIndex, "passportBack"),
      label: "Passport Back",
      title: "Passport Back",
      helper: "Click to upload (PDF / JPG / PNG)",
      required: false,
      icon: App_Url_New.DocumentIcons.passport,
      ocrFields: App_Url_New.OcrApiFields.PASSPORT_ADDRESS,
      combinedUploadGroup: getCombinedGroup(
        travellerIndex,
        "passport",
        "Passport",
        false,
        getTravellerFieldKey(travellerIndex, "passportCombined")
      ),
    },
    {
      key: getTravellerFieldKey(travellerIndex, "visaCopy"),
      label: "Visa",
      title: "Visa",
      helper: "Click to upload (PDF / JPG / PNG)",
      required: false,
      icon: App_Url_New.DocumentIcons.pdf,
      enableOcr: false,
    },
  ];
}

const TravelDocumentUploadStep = forwardRef<
  StepFormHandle<TravelDocumentUploadValues>,
  StepFormComponentProps<TravelDocumentUploadValues, TravelDocumentUploadStepExtraProps>
>(function TravelDocumentUploadStep(
  {
    defaultValues: incomingValues,
    isReadOnly = false,
    persistedDocuments,
    travelDetails: travelDetailsProp,
    onUploadComplete,
    isShowUploadGuidelines = true,
    showRequiredIdentificationTitle = true,
    onConfirmDocumentsChange,
    enableAutoConfirmWhenRequiredUploaded = false,
  },
  ref
) {
  const { travelDetails: reduxTravelDetails } = useTravelPostQuoteData();
  const travelDetails = travelDetailsProp ?? reduxTravelDetails;
  const travellers = useMemo(
    () => (travelDetails?.travellers?.length ? travelDetails.travellers : [{ name: "", dob: "" }]),
    [travelDetails?.travellers]
  );
  const [form, setForm] = useState<TravelDocumentUploadValues>(incomingValues ?? defaultValues);
  const [openTravellerIndex, setOpenTravellerIndex] = useState(0);
  const [hasSubmitAttempted, setHasSubmitAttempted] = useState(false);
  const [showConfirmError, setShowConfirmError] = useState(false);
  const [uploadErrors, setUploadErrors] = useState<Partial<Record<string, string>>>({});

  useEffect(() => {
    setForm(incomingValues ?? defaultValues);
    setHasSubmitAttempted(false);
    setShowConfirmError(false);
    setUploadErrors({});
  }, [incomingValues]);

  const travellerFields = useMemo(
    () => travellers.map((_, index) => createFields(index)),
    [travellers]
  );
  const hasMultipleTravellers = travellers.length > 1;

  useEffect(() => {
    if (!persistedDocuments) {
      return;
    }

    setForm((current) => {
      const nextValues = { ...current };

      travellerFields.flat().forEach((field) => {
        if (persistedDocuments[field.key]) {
          nextValues[field.key] = true;
        }
      });

      return nextValues;
    });
  }, [persistedDocuments, travellerFields]);

  const getUploadValidationErrors = (values: TravelDocumentUploadValues) =>
    travellerFields.flat().reduce<Partial<Record<string, string>>>((errors, field) => {
      if (field.required && values[field.key] !== true) {
        errors[field.key] = `${field.label || field.title} is required.`;
      }

      return errors;
    }, {});

  const hasAnyDocumentSelection = (values: TravelDocumentUploadValues) =>
    Object.entries(values).some(([key, checked]) => key !== "confirmDocuments" && checked === true);

  const normalizeUploadedValues = (values: TravelDocumentUploadValues) => {
    if (!enableAutoConfirmWhenRequiredUploaded) {
      return values;
    }

    const shouldConfirm = hasAnyDocumentSelection(values);

    if (values.confirmDocuments === shouldConfirm) {
      return values;
    }

    return {
      ...values,
      confirmDocuments: shouldConfirm,
    };
  };

  const syncValidationState = (nextValues: TravelDocumentUploadValues) => {
    if (!hasSubmitAttempted) {
      return;
    }

    setUploadErrors(getUploadValidationErrors(nextValues));
    setShowConfirmError(nextValues.confirmDocuments !== true);
  };

  useImperativeHandle(ref, () => ({
    submit: async () => {
      if (isReadOnly) {
        return true;
      }
      const nextUploadErrors = getUploadValidationErrors(form);
      const isConfirmValid = form.confirmDocuments === true;

      setHasSubmitAttempted(true);
      setUploadErrors(nextUploadErrors);
      setShowConfirmError(!isConfirmValid);

      const firstErrorField = Object.keys(nextUploadErrors)[0];
      if (firstErrorField) {
        const errorTravellerIndex = travellerFields.findIndex((fields) =>
          fields.some((field) => field.key === firstErrorField)
        );

        if (errorTravellerIndex >= 0) {
          setOpenTravellerIndex(errorTravellerIndex);
        }
      }

      return Object.keys(nextUploadErrors).length === 0 && isConfirmValid;
    },
    getValues: () => form,
  }));

  return (
    <div className="space-y-6">
      {showRequiredIdentificationTitle ? (
        <div className="mb-4">
          <h2 className="text-lg text-dark">Required Identification</h2>
        </div>
      ) : null}

      {hasMultipleTravellers ? (
        <div className="space-y-4">
          {travellers.map((traveller, travellerIndex) => {
            const isOpen = openTravellerIndex === travellerIndex;
            const fields = travellerFields[travellerIndex] || [];

            return (
              <CommonAccordion
                key={`${travellerIndex}-${traveller?.dob || ""}`}
                title={getTravellerLabel(travellerIndex, traveller)}
                isOpen={isOpen}
                onToggle={() => setOpenTravellerIndex(isOpen ? -1 : travellerIndex)}
              >
                <fieldset disabled={isReadOnly} className="contents">
                  <DocumentUploadGrid<TravelDocumentUploadValues>
                    fields={fields}
                    values={form}
                    disabled={isReadOnly}
                    onChange={(nextValues) => {
                      const normalizedValues = normalizeUploadedValues(nextValues);
                      setForm(normalizedValues);
                      syncValidationState(normalizedValues);
                    }}
                    fieldErrors={uploadErrors}
                    uploadEndpoint={App_Url_New.ApiEndPoint.documentUpload}
                    ocrEndpointPath={App_Url_New.ApiOcrEndPoint.ExtractDocument}
                    ocrFields={{
                      ai_engine: "openai",
                      ocr_engine: "easy",
                    }}
                    initialDocuments={persistedDocuments}
                    onUploadComplete={onUploadComplete}
                  />
                </fieldset>
              </CommonAccordion>
            );
          })}
        </div>
      ) : (
        <fieldset disabled={isReadOnly} className="contents">
          <DocumentUploadGrid<TravelDocumentUploadValues>
            fields={travellerFields[0] || []}
            values={form}
            disabled={isReadOnly}
            onChange={(nextValues) => {
              const normalizedValues = normalizeUploadedValues(nextValues);
              setForm(normalizedValues);
              syncValidationState(normalizedValues);
            }}
            fieldErrors={uploadErrors}
            uploadEndpoint={App_Url_New.ApiEndPoint.documentUpload}
            ocrEndpointPath={App_Url_New.ApiOcrEndPoint.ExtractDocument}
            ocrFields={{
              ai_engine: "openai",
              ocr_engine: "easy",
            }}
            initialDocuments={persistedDocuments}
            onUploadComplete={onUploadComplete}
          />
        </fieldset>
      )}

      {isShowUploadGuidelines && (
        <div className="rounded-xl border border-theme-border bg-theme-surface-subtle p-4 mt-5">
          <h3 className="text-sm font-semibold text-theme-text">Document guidelines</h3>
          <ul className="mt-3 list-disc space-y-2 pl-5 text-[11px] text-theme-text-soft">
            {guidelines.map((guideline) => (
              <li className="mb-0.5" key={guideline}>
                {guideline}
              </li>
            ))}
          </ul>
        </div>
      )}

      <fieldset disabled={isReadOnly} className="contents">
        <ConfirmationCheckbox
          checked={form.confirmDocuments === true}
          disabled={isReadOnly}
          error={
            showConfirmError
              ? "Please confirm the document upload before moving to the next step."
              : ""
          }
          onChange={async (checked) => {
            if (checked) {
              const nextUploadErrors = getUploadValidationErrors(form);
              const hasValidationErrors = Object.keys(nextUploadErrors).length > 0;

              setHasSubmitAttempted(true);
              setUploadErrors(nextUploadErrors);

              if (hasValidationErrors) {
                setShowConfirmError(true);
                setForm((current) => ({
                  ...current,
                  confirmDocuments: false,
                }));
                return;
              }
            }

            const nextValues = {
              ...form,
              confirmDocuments: checked,
            };

            setForm(nextValues);
            syncValidationState(nextValues);

            queueMicrotask(async () => {
              const accepted = await onConfirmDocumentsChange?.(checked);
              if (checked && accepted === false) {
                setForm((current) => {
                  const revertedValues = {
                    ...current,
                    confirmDocuments: false,
                  };
                  syncValidationState(revertedValues);
                  return revertedValues;
                });
              }
            });
          }}
        >
          I confirm that I have uploaded the required documents for all travellers.
        </ConfirmationCheckbox>
      </fieldset>
    </div>
  );
});

export default TravelDocumentUploadStep;
